<script>
  import { createEventDispatcher, onMount, onDestroy } from 'svelte';
  import { user, setSession } from '$stores/sessionStore';
  import { theme } from '$stores/themeStore';

  const dispatch = createEventDispatcher();

  export let isOpen = false;

  // Game constants
  const BOARD_WIDTH = 10;
  const BOARD_HEIGHT = 20;
  const CELL_SIZE = 25;

  // Game state
  let board = Array(BOARD_HEIGHT).fill().map(() => Array(BOARD_WIDTH).fill(0));
  let currentPiece = null;
  let nextPiece = null;
  let gameRunning = false;
  let gamePaused = false;
  let score = 0;
  let level = 1;
  let lines = 0;
  let dropTime = 1000; // milliseconds
  let lastDrop = 0;
  let animationFrame = null;
  let gameOverMessage = '';
  let isNewHighScore = false;
  let highScoreTimeout = null;
  let currentHighScore = 0;
  let highScoreClickCount = 0;
  let highScoreClickTimeout = null;

  // Tetris pieces (Tetrominoes)
  const PIECES = {
    I: { shape: [[1,1,1,1]], color: '#00ffff' },
    O: { shape: [[1,1],[1,1]], color: '#ffff00' },
    T: { shape: [[0,1,0],[1,1,1]], color: '#800080' },
    S: { shape: [[0,1,1],[1,1,0]], color: '#00ff00' },
    Z: { shape: [[1,1,0],[0,1,1]], color: '#ff0000' },
    J: { shape: [[1,0,0],[1,1,1]], color: '#0000ff' },
    L: { shape: [[0,0,1],[1,1,1]], color: '#ffa500' }
  };

  const PIECE_TYPES = Object.keys(PIECES);

  // Current piece state
  let pieceX = 0;
  let pieceY = 0;
  let pieceRotation = 0;

  // Initialize board
  function initBoard() {
    board = Array(BOARD_HEIGHT).fill().map(() => Array(BOARD_WIDTH).fill(0));
  }

  // Get random piece
  function getRandomPiece() {
    const type = PIECE_TYPES[Math.floor(Math.random() * PIECE_TYPES.length)];
    return {
      type,
      shape: PIECES[type].shape,
      color: PIECES[type].color
    };
  }

  // Rotate piece matrix 90 degrees clockwise
  function rotatePiece(piece) {
    const rotated = piece.shape[0].map((_, i) => 
      piece.shape.map(row => row[i]).reverse()
    );
    return { ...piece, shape: rotated };
  }

  // Check if piece position is valid
  function isValidPosition(piece, x, y, rotation = 0) {
    let testPiece = { ...piece };
    for (let r = 0; r < rotation; r++) {
      testPiece = rotatePiece(testPiece);
    }

    for (let row = 0; row < testPiece.shape.length; row++) {
      for (let col = 0; col < testPiece.shape[row].length; col++) {
        if (testPiece.shape[row][col]) {
          const newX = x + col;
          const newY = y + row;
          
          if (newX < 0 || newX >= BOARD_WIDTH || newY >= BOARD_HEIGHT) {
            return false;
          }
          if (newY >= 0 && board[newY][newX]) {
            return false;
          }
        }
      }
    }
    return true;
  }

  // Place piece on board
  function placePiece() {
    let piece = currentPiece;
    for (let r = 0; r < pieceRotation; r++) {
      piece = rotatePiece(piece);
    }

    for (let row = 0; row < piece.shape.length; row++) {
      for (let col = 0; col < piece.shape[row].length; col++) {
        if (piece.shape[row][col]) {
          const x = pieceX + col;
          const y = pieceY + row;
          if (y >= 0) {
            board[y][x] = piece.color;
          }
        }
      }
    }
  }

  // Clear completed lines
  function clearLines() {
    let linesCleared = 0;
    for (let row = BOARD_HEIGHT - 1; row >= 0; row--) {
      if (board[row].every(cell => cell !== 0)) {
        board.splice(row, 1);
        board.unshift(Array(BOARD_WIDTH).fill(0));
        linesCleared++;
        row++; // Check the same row again
      }
    }

    if (linesCleared > 0) {
      lines += linesCleared;
      // Scoring: 40 * (level + 1) for single line, 100 for double, 300 for triple, 1200 for tetris
      const lineScores = [0, 40, 100, 300, 1200];
      score += lineScores[linesCleared] * (level + 1);
      
      // Level up every 10 lines
      const newLevel = Math.floor(lines / 10) + 1;
      if (newLevel > level) {
        level = newLevel;
        dropTime = Math.max(50, 1000 - (level - 1) * 100); // Increase speed
      }
    }
  }

  // Spawn new piece
  function spawnPiece() {
    currentPiece = nextPiece || getRandomPiece();
    nextPiece = getRandomPiece();
    pieceX = Math.floor(BOARD_WIDTH / 2) - 1;
    pieceY = 0;
    pieceRotation = 0;

    if (!isValidPosition(currentPiece, pieceX, pieceY)) {
      gameOver();
    }
  }

  // Game over
  function gameOver() {
    gameRunning = false;
    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
    }

    // Set game over message
    gameOverMessage = `Game Over!\nScore: ${score}`;
    
    // Check if it's a new high score
    if ($user && score > currentHighScore) {
      isNewHighScore = true;
      saveHighScore();
      
      // Auto-hide high score overlay after 5 seconds
      if (highScoreTimeout) clearTimeout(highScoreTimeout);
      highScoreTimeout = setTimeout(() => {
        isNewHighScore = false;
      }, 5000);
    } else {
      // Auto-hide game over overlay after 5 seconds
      if (highScoreTimeout) clearTimeout(highScoreTimeout);
      highScoreTimeout = setTimeout(() => {
        gameOverMessage = '';
      }, 5000);
    }
  }

  // Load high score from backend
  async function loadHighScore() {
    if (!$user) {
      console.log('No user found, cannot load high score');
      return;
    }

    try {
      const response = await fetch(`/api/v1/users/highscore?userId=${$user.id}`, {
        method: 'GET',
      });

      if (response.ok) {
        const result = await response.json();
        console.log('High score load result:', result);
        currentHighScore = result.tetrisHighScore || 0;
      } else {
        console.error('Failed to load high score. Status:', response.status);
      }
    } catch (error) {
      console.error('Failed to load high score:', error);
    }
  }

  // Reset high score to 0
  async function resetHighScore() {
    if (!$user) {
      console.log('No user found, cannot reset high score');
      return;
    }

    try {
      const response = await fetch(`/api/v1/users/highscore?userId=${$user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tetrisHighScore: 0 }),
      });

      if (response.ok) {
        console.log('High score reset successfully');
        currentHighScore = 0;
        
        // Update the user store with the reset high score
        if ($user) {
          const updatedUser = { ...$user, tetrisHighScore: 0 };
          setSession(updatedUser, true); // Skip refresh to avoid conflicts
        }
      } else {
        const errorData = await response.text();
        console.error('Failed to reset high score. Status:', response.status, 'Error:', errorData);
      }
    } catch (error) {
      console.error('Failed to reset high score:', error);
    }
  }

  // Handle high score click sequence
  function handleHighScoreClick() {
    highScoreClickCount++;
    
    // Clear existing timeout
    if (highScoreClickTimeout) {
      clearTimeout(highScoreClickTimeout);
    }
    
    // Set timeout to reset click count
    highScoreClickTimeout = setTimeout(() => {
      highScoreClickCount = 0;
    }, 1000); // 1 second window for triple click
    
    // Check for triple click
    if (highScoreClickCount === 3) {
      console.log('Secret triple click detected! Resetting high score...');
      resetHighScore();
      highScoreClickCount = 0;
      if (highScoreClickTimeout) {
        clearTimeout(highScoreClickTimeout);
      }
    }
  }

  // Save high score to backend
  async function saveHighScore() {
    if (!$user) {
      console.log('No user found, cannot save high score');
      return;
    }

    console.log('Saving high score:', score, 'for user:', $user.id);
    
    try {
      const response = await fetch('/api/v1/users/highscore', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          userId: $user.id,
          tetrisHighScore: score 
        }),
      });

      console.log('High score save response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('High score save result:', result);
        
        // Update the current high score
        currentHighScore = score;
        
        // Update the user store with the new high score
        if ($user) {
          const updatedUser = { ...$user, tetrisHighScore: score };
          setSession(updatedUser, true); // Skip refresh to avoid conflicts
        }
      } else {
        const errorText = await response.text();
        console.error('Failed to save high score. Status:', response.status, 'Error:', errorText);
      }
    } catch (error) {
      console.error('Failed to save high score:', error);
    }
  }

  // Move piece
  function movePiece(dx, dy) {
    if (isValidPosition(currentPiece, pieceX + dx, pieceY + dy, pieceRotation)) {
      pieceX += dx;
      pieceY += dy;
      return true;
    }
    return false;
  }

  // Rotate piece
  function rotatePieceInGame() {
    const newRotation = (pieceRotation + 1) % 4;
    if (isValidPosition(currentPiece, pieceX, pieceY, newRotation)) {
      pieceRotation = newRotation;
    }
  }

  // Drop piece one row
  function dropPiece() {
    if (!movePiece(0, 1)) {
      placePiece();
      clearLines();
      spawnPiece();
    }
  }

  // Hard drop
  function hardDrop() {
    while (movePiece(0, 1)) {
      score += 2; // Bonus points for hard drop
    }
    dropPiece();
  }

  // Game loop
  function gameLoop(timestamp) {
    if (!gameRunning || gamePaused) return;

    if (timestamp - lastDrop > dropTime) {
      dropPiece();
      lastDrop = timestamp;
    }

    animationFrame = requestAnimationFrame(gameLoop);
  }

  // Start game
  function startGame() {
    initBoard();
    score = 0;
    level = 1;
    lines = 0;
    dropTime = 1000;
    gameRunning = true;
    gamePaused = false;
    gameOverMessage = '';
    isNewHighScore = false;
    spawnPiece();
    lastDrop = performance.now();
    animationFrame = requestAnimationFrame(gameLoop);
  }

  // Pause/Resume game
  function togglePause() {
    if (!gameRunning) return;
    gamePaused = !gamePaused;
    if (!gamePaused) {
      lastDrop = performance.now();
      animationFrame = requestAnimationFrame(gameLoop);
    }
  }

  // Handle keyboard input
  function handleKeydown(event) {
    if (!isOpen || !gameRunning || gamePaused) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        movePiece(-1, 0);
        break;
      case 'ArrowRight':
        event.preventDefault();
        movePiece(1, 0);
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (movePiece(0, 1)) {
          score += 1; // Bonus for soft drop
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        rotatePieceInGame();
        break;
      case ' ':
        event.preventDefault();
        hardDrop();
        break;
      case 'p':
      case 'P':
        event.preventDefault();
        togglePause();
        break;
    }
  }

  // Get rendered board with current piece
  function getRenderedBoard(boardState, currentPieceState, x, y, rotation) {
    const rendered = boardState.map(row => [...row]);
    
    if (currentPieceState) {
      let piece = currentPieceState;
      for (let r = 0; r < rotation; r++) {
        piece = rotatePiece(piece);
      }

      for (let row = 0; row < piece.shape.length; row++) {
        for (let col = 0; col < piece.shape[row].length; col++) {
          if (piece.shape[row][col]) {
            const blockX = x + col;
            const blockY = y + row;
            if (blockY >= 0 && blockY < BOARD_HEIGHT && blockX >= 0 && blockX < BOARD_WIDTH) {
              rendered[blockY][blockX] = piece.color;
            }
          }
        }
      }
    }

    return rendered;
  }

  // Get next piece preview
  function getNextPiecePreview(nextPieceState) {
    if (!nextPieceState) return [];
    const maxSize = 4;
    const preview = Array(maxSize).fill().map(() => Array(maxSize).fill(0));
    
    const offsetX = Math.floor((maxSize - nextPieceState.shape[0].length) / 2);
    const offsetY = Math.floor((maxSize - nextPieceState.shape.length) / 2);
    
    for (let row = 0; row < nextPieceState.shape.length; row++) {
      for (let col = 0; col < nextPieceState.shape[row].length; col++) {
        if (nextPieceState.shape[row][col]) {
          preview[offsetY + row][offsetX + col] = nextPieceState.color;
        }
      }
    }
    
    return preview;
  }

  function resetGameState() {
    // Stop any running game
    if (gameRunning) {
      gameRunning = false;
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    }
    
    // Clear timeouts
    if (highScoreTimeout) {
      clearTimeout(highScoreTimeout);
    }
    
    // Reset all game state
    initBoard();
    currentPiece = null;
    nextPiece = null;
    gameRunning = false;
    gamePaused = false;
    score = 0;
    level = 1;
    lines = 0;
    dropTime = 1000;
    pieceX = 0;
    pieceY = 0;
    pieceRotation = 0;
    gameOverMessage = '';
    isNewHighScore = false;
  }

  function closeModal() {
    if (gameRunning) {
      gameRunning = false;
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    }
    if (highScoreTimeout) {
      clearTimeout(highScoreTimeout);
    }
    if (highScoreClickTimeout) {
      clearTimeout(highScoreClickTimeout);
    }
    dispatch('close');
  }

  onMount(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleKeydown);
    }
    initBoard();
    // Don't auto-spawn piece - wait for user to start game
  });

  // Load high score and reset game state when modal opens
  $: if (isOpen && $user) {
    // Use tetrisHighScore from user store instead of making API call
    currentHighScore = $user.tetrisHighScore || 0;
    resetGameState();
  }

  onDestroy(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', handleKeydown);
    }
    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
    }
  });

  $: renderedBoard = getRenderedBoard(board, currentPiece, pieceX, pieceY, pieceRotation);
  $: nextPiecePreview = getNextPiecePreview(nextPiece);
</script>

{#if isOpen}
  <!-- Modal backdrop -->
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={closeModal}></div>
    <!-- Modal content -->
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 pt-2 max-w-4xl w-full mx-4 isolate" on:click={(e) => e.stopPropagation()}>
      <!-- Header -->
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white">🎮 Tetris</h2>
        <button
          on:click={closeModal}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
        >
          ×
        </button>
      </div>

      <!-- Game area -->
      <div class="flex flex-col lg:flex-row gap-6 bg">
        <!-- Game board -->
        <div class="flex-1 flex justify-center">
          <div class="bg-gray-400/10 dark:bg-gray-900 p-4 rounded-lg flex justify-center">
            <div 
              class="grid gap-1 border-2 bg-gray-300/10 dark:border-gray-600/50"
              style="grid-template-columns: repeat({BOARD_WIDTH}, {CELL_SIZE}px);"
            >
              {#each renderedBoard as row}
                {#each row as cell}
                  <div
                    class="border border-gray-200/30 dark:border-gray-700"
                    style="width: {CELL_SIZE}px; height: {CELL_SIZE}px; background-color: {cell || ($theme === 'dark' ? '#1f2937' : '#ffffff')};"
                  ></div>
                {/each}
              {/each}
            </div>
          </div>
        </div>

        <!-- Game info panel -->
        <div class="w-full lg:w-64 space-y-4">
          <!-- Score panel -->
          <div class="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg relative">
            <h3 class="font-bold text-gray-900 dark:text-white mb-2">Score</h3>
            <div class="text-xl font-mono text-blue-600 dark:text-blue-400">{score.toLocaleString()}</div>
            <div 
              class="text-sm text-gray-600 dark:text-gray-400 mt-1 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              on:click={handleHighScoreClick}
            >
              Your High: {currentHighScore.toLocaleString()}
            </div>
            
            <!-- Game Over or High Score overlay -->
            {#if gameOverMessage}
              {#if isNewHighScore}
                <!-- High score overlay -->
                <div class="absolute inset-0 bg-yellow-400 dark:bg-yellow-600 bg-opacity-90 rounded-lg flex items-center justify-center z-10">
                  <div class="text-center">
                    <div class="text-2xl font-bold text-yellow-900 dark:text-yellow-100">🎉</div>
                    <div class="text-sm font-bold text-yellow-900 dark:text-yellow-100">NEW HIGH SCORE!</div>
                  </div>
                </div>
              {:else}
                <!-- Game Over overlay -->
                <div class="absolute inset-0 bg-red-400 dark:bg-red-600 bg-opacity-90 rounded-lg flex items-center justify-center z-10">
                  <div class="text-center">
                    <div class="text-2xl font-bold text-red-900 dark:text-red-100">💀</div>
                    <div class="text-sm font-bold text-red-900 dark:text-red-100">GAME OVER!</div>
                    <div class="text-xs text-red-800 dark:text-red-200 mt-1">Score: {score.toLocaleString()}</div>
                  </div>
                </div>
              {/if}
            {/if}
          </div>

          <!-- Level and Lines -->
          <div class="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <h4 class="font-bold text-gray-900 dark:text-white text-sm">Level</h4>
                <div class="text-lg font-mono text-green-600 dark:text-green-400">{level}</div>
              </div>
              <div>
                <h4 class="font-bold text-gray-900 dark:text-white text-sm">Lines</h4>
                <div class="text-lg font-mono text-purple-600 dark:text-purple-400">{lines}</div>
              </div>
            </div>
          </div>

          <!-- Next piece -->
          <div class="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
            <h3 class="font-bold text-gray-900 dark:text-white mb-2">Next</h3>
            <div class="bg-gray-400/10 dark:bg-gray-900 p-2 rounded">
              <div 
                class="grid gap-1 mx-auto"
                style="grid-template-columns: repeat(4, 20px); width: 80px;"
              >
                {#each (nextPiecePreview.length > 0 ? nextPiecePreview : Array(4).fill().map(() => Array(4).fill(0))) as row}
                  {#each row as cell}
                    <div
                      class="border border-gray-300 dark:border-gray-700"
                      style="width: 20px; height: 20px; background-color: {cell || ($theme === 'dark' ? '#1f2937' : '#ffffff')};"
                    ></div>
                  {/each}
                {/each}
              </div>
            </div>
          </div>

          <!-- Controls -->
          <div class="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
            <h3 class="font-bold text-gray-900 dark:text-white mb-2">Controls</h3>
            <div class="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <div>← → Move</div>
              <div>↓ Soft drop</div>
              <div>↑ Rotate</div>
              <div>Space Hard drop</div>
              <div>P Pause</div>
            </div>
          </div>

          <!-- Game controls -->
          <div class="space-y-2">
            {#if !gameRunning}
              <button
                on:click={startGame}
                class="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition-colors"
              >
                {gameOverMessage ? 'Play Again' : 'Start Game'}
              </button>
            {:else}
              <button
                on:click={togglePause}
                class="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded transition-colors"
              >
                {gamePaused ? 'Resume' : 'Pause'}
              </button>
            {/if}
          </div>


        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  /* Ensure the modal is above everything else */
  :global(.tetris-modal) {
    z-index: 9999;
  }
</style>
